import type { EstateRow } from '@acasa/shared'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { CreateBriefingDialog } from './create-dialog'
import { briefingStand } from './format'
import { BriefingStatusChip } from './status-chip'
import { useBriefings } from './use-briefings'

/** How many runs the Unterlagen row lists before pointing at the full list. */
const SHOWN = 4

/**
 * The object's briefings, in an opened Unterlagen row. One query, keyed on the
 * estate, mounted only while the row is open — no onOffice call until somebody
 * actually starts a new one.
 */
export function EstateBriefings({ estate }: { estate: EstateRow }) {
  const { data, isPending } = useBriefings(estate.estateId)
  const [creating, setCreating] = useState(false)

  const briefings = data?.briefings ?? []
  const disabled = !(data?.configured ?? false) || (data?.limitReached ?? false)
  const disabledReason = !data
    ? undefined
    : !data.configured
      ? 'Objekt-Briefings sind nicht konfiguriert'
      : data.limitReached
        ? 'Monatslimit für KI-Nutzung erreicht'
        : undefined

  return (
    <div className="border-line mt-4 border-t pt-3">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <p className="text-faint text-[10px] tracking-wider uppercase">Briefings</p>
        <button
          type="button"
          disabled={disabled}
          title={disabledReason}
          onClick={() => setCreating(true)}
          className="text-brand text-[11px] underline underline-offset-2 disabled:opacity-40"
        >
          Neues Briefing
        </button>
      </div>

      {isPending && <p className="text-faint text-xs">wird geladen …</p>}
      {data && briefings.length === 0 && (
        <p className="text-faint text-xs">Noch kein Briefing für dieses Objekt.</p>
      )}
      {briefings.length > 0 && (
        <ul className="space-y-1">
          {briefings.slice(0, SHOWN).map((briefing) => (
            <li key={briefing.id}>
              <Link
                to={`/briefings/${briefing.id}`}
                className="hover:text-brand flex items-center gap-2 text-xs"
              >
                <span className="text-ink tnum">Stand {briefingStand(briefing)}</span>
                <BriefingStatusChip status={briefing.status} />
                {/* Only the CURRENT one: the field in onOffice holds a single url. */}
                {briefing.onOfficeLink.current && (
                  <span className="text-faint text-[10px] tracking-wider uppercase">
                    in onOffice
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
      {briefings.length > SHOWN && (
        <Link
          to={`/briefings?estate=${estate.estateId}`}
          className="text-faint hover:text-ink mt-1 inline-block text-[11px]"
        >
          alle {briefings.length} anzeigen
        </Link>
      )}

      {creating && (
        <CreateBriefingDialog
          estate={{
            estateId: estate.estateId,
            externalNumber: estate.externalNumber,
            label: [estate.title, estate.city].filter(Boolean).join(', ') || 'ohne Bezeichnung',
          }}
          onClose={() => setCreating(false)}
        />
      )}
    </div>
  )
}
